import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import Grid from "@mui/material/Grid";
import { FlexStart } from "components/FlexBox/FlexBox";

const thumbsContainer = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  marginTop: 16
};

const thumb = {
  display: 'inline-flex',
  borderRadius: 2,
  border: '1px solid #eaeaea',
  marginBottom: 8,
  marginRight: 8,
  width: 100,
  height: 100,
  padding: 4,
  boxSizing: 'border-box'
};

const thumbInner = {
  display: 'flex',
  minWidth: 0,
  overflow: 'hidden'
};

const img = {
  display: 'block',
  width: 'auto',
  height: '100%'
};


const DEFAULT_MAX_TOTAL_BYTES = 15 * 1024 * 1024; // 15MB total
const DEFAULT_MAX_FILE_BYTES = 15 * 1024 * 1024; // keep per-file sane by default

const toMB = (bytes) => bytes / 1024 / 1024;

const ImageUploader = ({
  files,
  setFiles,
  maxFiles,
  maxTotalBytes = DEFAULT_MAX_TOTAL_BYTES,
  maxFileBytes = DEFAULT_MAX_FILE_BYTES,
}) => {
  const filesRef = useRef(files || []);
  useEffect(() => {
    filesRef.current = files || [];
  }, [files]);

  const [manualRejections, setManualRejections] = useState([]);

  const currentTotalBytes = useMemo(() => {
    return (files || []).reduce((sum, f) => sum + (f?.size || 0), 0);
  }, [files]);

  const remainingBytes = Math.max(0, maxTotalBytes - currentTotalBytes);
  const isAtTotalLimit = remainingBytes <= 0;

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    accept: {
      "image/*": [".png", ".jpeg", ".jpg", ".jfif", ".webp"],
    },
    // We enforce total-size + optional maxFiles ourselves for clearer UX.
    disabled: isAtTotalLimit,
    multiple: true,
    onDrop: (acceptedFiles) => {
      // Clear manual rejections from previous interaction
      setManualRejections([]);

      const existing = filesRef.current || [];
      const existingTotal = existing.reduce((sum, f) => sum + (f?.size || 0), 0);

      let runningTotal = existingTotal;
      const toAdd = [];
      const newManualRejections = [];

      for (const file of acceptedFiles) {
        const wouldExceedCount =
          typeof maxFiles === "number" && existing.length + toAdd.length >= maxFiles;
        if (wouldExceedCount) {
          newManualRejections.push({
            file,
            errors: [{ code: "too-many-files", message: `Too many files. Max is ${maxFiles}.` }],
          });
          continue;
        }

        if (file.size > maxFileBytes) {
          newManualRejections.push({
            file,
            errors: [
              {
                code: "file-too-large",
                message: `File is larger than ${toMB(maxFileBytes).toFixed(0)}MB.`,
              },
            ],
          });
          continue;
        }

        if (runningTotal + file.size > maxTotalBytes) {
          const remaining = Math.max(0, maxTotalBytes - runningTotal);
          newManualRejections.push({
            file,
            errors: [
              {
                code: "total-size-exceeded",
                message: `Total selection would exceed ${toMB(maxTotalBytes).toFixed(
                  0
                )}MB. Remaining: ${toMB(remaining).toFixed(2)}MB.`,
              },
            ],
          });
          continue;
        }

        const index = toAdd.length;
        const fileId = file.id || `${file.name}_${Date.now()}_${index}`;
        toAdd.push(
          Object.assign(file, {
            preview: URL.createObjectURL(file),
            id: fileId,
          })
        );
        runningTotal += file.size;
      }

      if (newManualRejections.length > 0) setManualRejections(newManualRejections);
      if (toAdd.length > 0) setFiles([...existing, ...toAdd]);
    },
  });

  // Ensure all files have IDs
  const filesWithIds = files.map((file, index) => {
    if (!file.id) {
      return Object.assign(file, {
        id: file.name + '_' + Date.now() + '_' + index
      });
    }
    return file;
  });

  const acceptedFileItems = filesWithIds.map((file, index) => (
    <li key={file.id || index}>
      {file.name} - {file.size/1000000 < 0.01 ? `${(file.size/1000).toFixed(2)} KB` : `${(file.size/1000000).toFixed(2)} MB`}
    </li>
  ));

  const combinedRejections = [...fileRejections, ...manualRejections];

  const fileRejectionItems = combinedRejections.map(({ file, errors }) => { 
   return (
     <li key={file.path}>
          {file.path} - {toMB(file.size).toFixed(2)} MB
          <ul>
            {errors.map(e => <li key={e.code}>{e.message}</li>)}
         </ul>

     </li>
   ) 
  });
  
  const removeFile = (fileId) => {
    setFiles((prev) => {
      const list = prev || [];
      const fileToRemove = list.find((f) => {
        const currentId = f.id || f.name;
        return currentId === fileId;
      });
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return list.filter((file) => {
        const currentId = file.id || file.name;
        return currentId !== fileId;
      });
    });
  };

  const thumbs = filesWithIds.map((file, index) => {
    const fileId = file.id || file.name + '_' + index;
    return (
      <Box key={fileId} sx={{ marginBottom: 2, border: '1px solid #eaeaea', borderRadius: 1, padding: 1, width: '100%', maxWidth: '300px' }}>
        <div style={thumb}>
          <div style={thumbInner}>
            <img
              src={file.preview}
              style={img}
              alt={`Preview ${index + 1}`}
              onLoad={() => { if (file.preview) URL.revokeObjectURL(file.preview)}}
            />
          </div>
        </div>
        <Typography
          variant="caption"
          onClick={() => removeFile(fileId)}
          sx={{
            cursor: 'pointer',
            color: 'error.main',
            display: 'block',
            marginTop: 0.5,
            textDecoration: 'underline'
          }}
        >
          Remove
        </Typography>
      </Box>
    );
  });

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => {
      files.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [files]);

  return (
    <section className="container">
      <div {...getRootProps({className: 'dropzone'})}>
        <Box sx={{
            border: "2px dashed black",
            padding: "20px"
        }}>
            <input {...getInputProps()} />
            <Typography>
              Drag and drop images here, or click to select files.
              {typeof maxFiles === "number" && (
                <>
                  {" "}
                  Max {maxFiles} images.
                </>
              )}
              {" "}
              Max total {toMB(maxTotalBytes).toFixed(0)}MB per selection (currently {toMB(currentTotalBytes).toFixed(2)}MB).
              {" "}
              {isAtTotalLimit && "Total limit reached — remove a file to add more."}
            </Typography>
        </Box>
      </div>
      <Grid style={thumbsContainer}>
        <FlexStart>
            {thumbs}
        </FlexStart>

        <Grid>
            {filesWithIds.length > 0 && (
                <Grid>
                    <Typography variant="h4" sx={{color:"#0759e8", fontSize: 15}}>Accepted files ({filesWithIds.length})</Typography>
                    <ul>{acceptedFileItems}</ul>
                </Grid>
            )}
        </Grid>
        <Grid>
            {fileRejectionItems.length > 0 && (
                <Grid>
                    <Typography variant="h4" sx={{color:"#e80707", fontSize: 15}}>Rejected files</Typography>
                    <ul>{fileRejectionItems}</ul>
                </Grid>
            )}
        </Grid>
      </Grid>
    </section>
  );
}

export default ImageUploader;